// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Professional | Category: Player Disputes & Escalation
// 25 questions | Difficulty: E=Easy, M=Medium, H=Hard

export const PLAYER_DISPUTES_QUESTIONS = [

  // ── EASY (5 questions) ──────────────────────────────────────────────────

  {
    id: 'pd_001', track: 'professional', category: 'player_disputes', difficulty: 'easy',
    question: 'Two players disagree about the bet amount. What do you do?',
    options: [
      'A) Side with the player who has more chips',
      'B) Make a ruling based on their own judgment',
      'C) Reconstruct the action, state what they observed, and call the floor if unresolved',
      'D) Ask the other players at the table to vote on the correct amount'
    ],
    correct: 'C',
    explanation: 'The dealer reconstructs what they observed and states it clearly. If the dispute cannot be resolved, the floor is called. The dealer does not take sides or make binding rulings on disputed amounts.'
  },

  {
    id: 'pd_002', track: 'professional', category: 'player_disputes', difficulty: 'easy',
    question: 'A player shouts aggressively at another player. What is the dealer\'s immediate response?',
    options: [
      'A) Ask both players to leave the table',
      'B) Firmly but calmly ask the player to lower their voice and call the floor',
      'C) Ignore it — player conflicts are not the dealer\'s concern',
      'D) Side with the player being shouted at'
    ],
    correct: 'B',
    explanation: 'The dealer should immediately and calmly ask the aggressive player to lower their voice. The floor should be notified. The dealer maintains a calm, authoritative presence without escalating the situation.'
  },

  {
    id: 'pd_003', track: 'professional', category: 'player_disputes', difficulty: 'easy',
    question: 'A player believes they were dealt an incorrect number of cards. What should the dealer do first?',
    options: [
      'A) Ask the player to count their cards face-down',
      'B) Immediately redeal the hand',
      'C) Ask other players if they noticed anything unusual',
      'D) Count the remaining deck to see if the number is correct'
    ],
    correct: 'A',
    explanation: 'The first step is to verify the claim — ask the player to fan their cards face-down to count them without revealing their values. Only after verification is the appropriate action (misdeal, correction, or continuation) determined.'
  },

  {
    id: 'pd_004', track: 'professional', category: 'player_disputes', difficulty: 'easy',
    question: 'What is the dealer\'s role when the floor is called to make a ruling?',
    options: [
      'A) Argue for the ruling they believe is correct',
      'B) Present the facts of what occurred as objectively and completely as possible',
      'C) Step away from the table while the floor handles it',
      'D) Let the players explain the situation themselves'
    ],
    correct: 'B',
    explanation: 'The dealer presents the facts objectively — what they observed, in what order, without editorial or advocacy for either player. The dealer is a witness to the action, not an advocate. The floor makes the ruling.'
  },

  {
    id: 'pd_005', track: 'professional', category: 'player_disputes', difficulty: 'easy',
    question: 'A player claims another player acted out of turn to gain information. What do you do?',
    options: [
      'A) Ignore the claim and continue play',
      'B) Acknowledge the claim, note what occurred, and call the floor',
      'C) Penalize the player who acted out of turn without calling the floor',
      'D) Ask the two players to resolve it themselves'
    ],
    correct: 'B',
    explanation: 'The dealer acknowledges the claim, notes what happened (who acted, in what position, what the action was), and calls the floor. Allegations of improper advantage require a floor ruling — the dealer does not adjudicate conduct violations.'
  },

  // ── MEDIUM (12 questions) ───────────────────────────────────────────────

  {
    id: 'pd_006', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player claims their verbal declaration was a joke and they did not intend to fold. The dealer believes the declaration was genuine. What happens?',
    options: [
      'A) Give the player the benefit of the doubt — keep their hand alive',
      'B) The fold stands — verbal declarations are binding regardless of claimed intent',
      'C) Call the floor to assess whether the declaration appeared genuine',
      'D) Ask other players at the table whether the declaration seemed genuine'
    ],
    correct: 'C',
    explanation: 'While verbal declarations are generally binding, the dealer calls the floor when there is ambiguity about intent. The floor assesses the situation and makes the ruling. The dealer should not unilaterally decide contested intent questions.'
  },

  {
    id: 'pd_007', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'Two players are arguing about a hand that occurred three hands ago. Neither was involved in the current hand. What does the dealer do?',
    options: [
      'A) Stop the current hand to resolve the old dispute',
      'B) Ask the arguing players to stop and focus on the current hand; call the floor if they persist',
      'C) Ignore them — the dispute is irrelevant to the current hand',
      'D) Ask both players to leave the table until the dispute is resolved'
    ],
    correct: 'B',
    explanation: 'The dealer asks the players to stop arguing at the table, particularly during an active hand. If they persist, the floor is called. Arguments about past hands should not disrupt the current game.'
  },

  {
    id: 'pd_008', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player accuses another of cheating. How do you handle this?',
    options: [
      'A) Investigate the allegation themselves',
      'B) Dismiss the accusation to avoid disruption',
      'C) Immediately call the floor — cheating allegations require management/security involvement',
      'D) Ask the accused player to defend themselves'
    ],
    correct: 'C',
    explanation: 'Cheating allegations are serious and must be escalated immediately to the floor and management. The dealer does not investigate or dismiss. Play may be halted pending the investigation. This protects the integrity of the game and all parties involved.'
  },

  {
    id: 'pd_009', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player refuses to show your hand at showdown after being called, claiming they have the best hand and the other player should show first. What is the ruling?',
    options: [
      'A) The refusing player\'s hand is dead — they must show or fold',
      'B) The last aggressor must show first; if this player was the last aggressor they must show',
      'C) The other player must show first regardless',
      'D) Both players must show simultaneously'
    ],
    correct: 'B',
    explanation: 'Showdown order is determined by the last aggressive action. The last bettor or raiser must show first. If this player was the last aggressor, they must show their hand. Refusing to show after being called results in the hand being declared dead.'
  },

  {
    id: 'pd_010', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player makes a large bet and another player says "I\'ll look you up" then begins counting chips slowly. Other players at the table are complaining. What do you do?',
    options: [
      'A) Nothing — players may take as long as needed',
      'B) Remind the player to act in a reasonable time; if complaint continues, inform the floor',
      'C) Fold the slow player\'s hand',
      'D) Call a two-minute clock immediately'
    ],
    correct: 'B',
    explanation: 'The dealer may remind a player that the game requires reasonable pace. If the slow play continues and players are complaining, the floor is informed. Only the floor can call an official clock — the dealer sets the stage by notifying the floor.'
  },

  {
    id: 'pd_011', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player throws their cards at the dealer in frustration after losing a big pot. What is the correct response?',
    options: [
      'A) Apologize to the player and continue',
      'B) Firmly tell the player that throwing cards is not acceptable and call the floor',
      'C) Ignore it — losing players are often emotional',
      'D) Warn the player once and assess a penalty on the second offense'
    ],
    correct: 'B',
    explanation: 'Throwing cards at a dealer is never acceptable. The dealer calmly but firmly states this and calls the floor immediately. No apology is warranted — the dealer is the victim of the conduct, not the cause.'
  },

  {
    id: 'pd_012', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'Two players run a hand twice (run it twice). You are unsure how to split the pots. What do you do?',
    options: [
      'A) Run it twice without authorization',
      'B) Confirm the agreement is permitted by the event rules, then execute per the floor\'s guidance',
      'C) Run it twice — it is always permitted in cash games',
      'D) Decline and run it once — running twice is never permitted'
    ],
    correct: 'B',
    explanation: '"Running it twice" (dealing the remaining board cards twice and splitting the pot) is only permitted if the event rules allow it. The dealer confirms with the floor whether it is permitted before executing. Rules vary by event.'
  },

  {
    id: 'pd_013', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player claims the dealer dealt them the wrong card and they would have won with the correct card. What is the ruling?',
    options: [
      'A) Redeal the hand from that point',
      'B) No correction is possible — deal with it and move on',
      'C) The floor investigates; if an error occurred, the pot may be adjusted',
      'D) Give the player a free hand as compensation'
    ],
    correct: 'B',
    explanation: 'There is no mechanism to redeal a card based on a player\'s claim about what they "should have" received. Cards are dealt randomly — there is no "correct" card. The hand stands. If there was an actual dealing error (procedural), that is a separate matter handled by the floor.'
  },

  {
    id: 'pd_014', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'English-only rules are in effect. Two players are speaking in another language during a hand. How do you respond?',
    options: [
      'A) Ignore it — the rule is unenforceable',
      'B) Remind the players of the English-only rule and ask them to speak English during the hand',
      'C) Immediately call the floor and stop the hand',
      'D) Ask the players to translate what they said'
    ],
    correct: 'B',
    explanation: 'When English-only rules are in effect, the dealer reminds players of the rule and asks them to speak English during the hand. A first occurrence typically warrants a reminder. Persistent violations are escalated to the floor.'
  },

  {
    id: 'pd_015', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player wins a pot with a flush but another player insists the winner had a straight, not a flush. The cards have already been mucked. What happens?',
    options: [
      'A) The winning player keeps the pot — their declaration stands',
      'B) Retrieve the cards from the muck if possible and verify; call the floor',
      'C) Award the pot to the objecting player since the winner can\'t prove their hand',
      'D) Split the pot since the hand cannot be verified'
    ],
    correct: 'B',
    explanation: 'The dealer attempts to retrieve the cards from the muck and calls the floor. If the cards can be identified and the hand was misread, the floor corrects the award. If cards cannot be retrieved, the floor makes the best ruling possible with available information.'
  },

  {
    id: 'pd_016', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player complains that you are dealing too fast for them to see their cards properly. What is the appropriate response?',
    options: [
      'A) Tell the player to deal their own cards if they want them slower',
      'B) Ignore the complaint and maintain pace',
      'C) Acknowledge the feedback and deal at a pace that gives players reasonable time to see their cards',
      'D) Call the floor to determine the correct dealing speed'
    ],
    correct: 'C',
    explanation: 'Players must be able to see their cards. A reasonable dealing pace is required. The dealer should acknowledge the feedback and ensure cards are pitched at a speed that allows players to see them. Speed is a balance between game flow and player needs.'
  },

  {
    id: 'pd_017', track: 'professional', category: 'player_disputes', difficulty: 'medium',
    question: 'A player goes on tilt after a bad beat and starts making verbal comments about other players\' play. Other players are uncomfortable. What do you do?',
    options: [
      'A) Allow the player to vent — bad beats are part of the game',
      'B) Ask the player to refrain from commenting on others\' play and call the floor if they persist',
      'C) Tell the other players they need to toughen up',
      'D) Offer the tilting player a break from the table'
    ],
    correct: 'B',
    explanation: 'Commenting negatively on others\' play ("slow roll," "terrible call," etc.) creates a hostile environment. The dealer asks the player to refrain. If they persist, the floor is called. The dealer cannot offer or compel breaks — that is the floor\'s authority.'
  },

  // ── HARD (8 questions) ──────────────────────────────────────────────────

  {
    id: 'pd_018', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'A player claims they were never given the option to act — the dealer skipped over them preflop. The claim is made after the flop has been dealt and action has begun. What is the correct procedure?',
    options: [
      'A) The hand continues — the player should have spoken up earlier',
      'B) Stop the hand immediately, give the skipped player their option, and re-evaluate the hand from that point',
      'C) Call the floor and halt action — a skipped player is a significant error requiring a ruling',
      'D) Offer the skipped player a free hand on the next deal as compensation'
    ],
    correct: 'C',
    explanation: 'A skipped player is a significant procedural error. The dealer calls the floor immediately. The floor determines whether the hand can continue (if the skipped player\'s action would not have affected subsequent action) or must be redealt.'
  },
  {
    id: 'pd_020', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'A highly experienced player is coaching a newcomer at the table between hands. During the hand, they start to whisper advice to the newcomer. What is the ruling?',
    options: [
      'A) Coaching between hands is fine; during a hand it violates one-player-to-a-hand rules',
      'B) Coaching at any time is permitted since both players consented',
      'C) Only the newcomer can be penalized — not the advisor',
      'D) Coaching is only prohibited in tournament final tables'
    ],
    correct: 'A',
    explanation: 'The one-player-to-a-hand rule prohibits advice or collusion during an active hand. Coaching during a hand is a violation for both parties. The dealer stops the coaching and calls the floor if it continues. Between-hand table talk is generally permitted.'
  },

  {
    id: 'pd_021', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'A player is found to be using a real-time odds calculator on their phone during a hand. What is the appropriate response?',
    options: [
      'A) Confiscate the phone and continue play',
      'B) Warn the player and ask them to put the phone away',
      'C) Call the floor immediately — electronic assistance during a hand is a serious violation',
      'D) Allow it — phone use rules vary by event'
    ],
    correct: 'C',
    explanation: 'Using electronic assistance to calculate odds during a hand is a serious integrity violation in tournament poker. The dealer immediately calls the floor. Penalties range from hand death to disqualification depending on the severity and event rules.'
  },

  {
    id: 'pd_022', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'Two players at the final table appear to be colluding — one is signaling their hole cards to the other. The dealer suspects this but cannot be certain. What do you do?',
    options: [
      'A) Accuse the players publicly to deter further collusion',
      'B) Ignore it unless they have clear proof',
      'C) Discretely notify the floor of their observations without making an accusation',
      'D) Ask the suspected players directly if they are colluding'
    ],
    correct: 'C',
    explanation: 'The dealer notes their observations and discretely informs the floor. The dealer does not make public accusations, confront the players, or ignore a genuine suspicion. Management and security handle the investigation. The dealer\'s job is to report, not accuse.'
  },

  {
    id: 'pd_023', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'A player wins a large pot and another player says "nice hand — you really got lucky there." The winner responds with an expletive. Both players escalate verbally. What is the dealer\'s order of priority?',
    options: [
      'A) Protect the winner since the other player started it',
      'B) Stop play, call the floor, ask both players to calm down, do not assign blame',
      'C) Ask the witnesses what happened and rule based on majority opinion',
      'D) Continue dealing to de-escalate through game action'
    ],
    correct: 'B',
    explanation: 'The dealer stops action, calls the floor, and asks both players to calm down. Assigning blame is not the dealer\'s role — who started it is irrelevant to the immediate need to de-escalate. The floor investigates and applies penalties as warranted.'
  },

  {
    id: 'pd_024', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'A player makes a bet, another player calls, and then the original bettor asks "can I take the bet back?" before any more action. The caller has not yet moved your chips. Can the bet be retracted?',
    options: [
      'A) Yes — if the caller hasn\'t moved chips, the bet is still in negotiation',
      'B) No — once a bet is made it is binding; only the floor can rule otherwise',
      'C) Yes — both players must agree, and if the caller agrees the bet is void',
      'D) Only the floor can authorize a bet retraction'
    ],
    correct: 'B',
    explanation: 'A bet once made is binding. The bettor cannot retract it regardless of whether the caller has moved chips yet. The caller moving chips is their action — it has no bearing on whether the original bet is binding.'
  },

  {
    id: 'pd_025', track: 'professional', category: 'player_disputes', difficulty: 'hard',
    question: 'A player is eliminated and becomes abusive toward the dealer, blaming them for a dealing error that the dealer did not make. What is the escalation protocol?',
    options: [
      'A) Apologize to the player to defuse the situation even if no error was made',
      'B) Remain calm, do not engage with false accusations, call the floor, and if behavior continues call security',
      'C) Defend yourself by explaining why no error was made',
      'D) Ask other players to confirm no error was made to validate your position'
    ],
    correct: 'B',
    explanation: 'The dealer remains calm and professional, does not apologize for something they did not do (which implies guilt), and calls the floor. If the behavior escalates to threats or physical aggression, security is called. The dealer never argues with an abusive player — escalation is management\'s responsibility.'
  },

];

export default PLAYER_DISPUTES_QUESTIONS;
